import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import {
  colorBorder,
  colorSurfaceHover,
  colorTextMuted,
  colorTextPrimary,
  colorTextSecondary,
} from "src/design/tokens";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import {
  useAttachRestaurants,
  useDetachRestaurants,
} from "src/data/admin/queries/organization";

interface AttachRestaurantsDialogProps {
  open: boolean;
  orgId: string;
  currentRestaurantIds: string[];
  onClose: () => void;
  onSaved: () => void;
}

export const AttachRestaurantsDialog = ({
  open,
  orgId,
  currentRestaurantIds,
  onClose,
  onSaved,
}: AttachRestaurantsDialogProps) => {
  const { restaurants, isLoading: restaurantsLoading } =
    useContext(RestaurantContext);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(currentRestaurantIds)
  );
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(new Set(currentRestaurantIds));
      setSearch("");
      setError(null);
    }
  }, [open, currentRestaurantIds]);

  const attachMutation = useAttachRestaurants();
  const detachMutation = useDetachRestaurants();

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return restaurants.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q) ||
        r.quartier.toLowerCase().includes(q)
    );
  }, [restaurants, search]);

  const handleToggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const currentSet = new Set(currentRestaurantIds);
    const toAttach = [...selected].filter((id) => !currentSet.has(id));
    const toDetach = currentRestaurantIds.filter((id) => !selected.has(id));

    if (toAttach.length === 0 && toDetach.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      if (toAttach.length > 0) {
        await attachMutation.mutateAsync({ id: orgId, restaurantIds: toAttach });
      }
      if (toDetach.length > 0) {
        await detachMutation.mutateAsync({ id: orgId, restaurantIds: toDetach });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isSaving && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Manage Restaurants</DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by name, city or quartier…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {restaurantsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" sx={{ color: colorTextMuted, py: 4, textAlign: "center" }}>
            {search ? "No restaurants match your search." : "No restaurants available."}
          </Typography>
        ) : (
          <Box
            sx={{
              border: `1px solid ${colorBorder}`,
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 400,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600, color: colorTextMuted }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorTextMuted }}>
                    City
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorTextMuted }}>
                    Quartier
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((restaurant) => {
                  const isChecked = selected.has(restaurant.id);
                  return (
                    <TableRow
                      key={restaurant.id}
                      hover
                      onClick={() => handleToggle(restaurant.id)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: isChecked ? colorSurfaceHover : undefined,
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          size="small"
                          onChange={() => handleToggle(restaurant.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell
                        sx={{ color: colorTextPrimary, fontWeight: isChecked ? 600 : 400 }}
                      >
                        {restaurant.name}
                      </TableCell>
                      <TableCell sx={{ color: colorTextSecondary }}>
                        {restaurant.city}
                      </TableCell>
                      <TableCell sx={{ color: colorTextMuted }}>
                        {restaurant.quartier}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {error && (
          <Typography variant="caption" sx={{ color: "error.main", mt: 1, display: "block" }}>
            {error}
          </Typography>
        )}

        <Typography variant="caption" sx={{ color: colorTextMuted, mt: 1, display: "block" }}>
          {selected.size} restaurant{selected.size !== 1 ? "s" : ""} selected
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton color="inverted" onClick={onClose} disabled={isSaving}>
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={isSaving}
          onSubmit={handleSave}
          label="Save"
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
